// src/odoo/customers/components/CustomerOverviewCards.tsx
// All comments are in English.

import React from "react";
import type { CustomerOverview, DateRangeQuery } from "../api/overviewApi";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface Props {
  overview?: CustomerOverview;
  isLoading: boolean;
  error?: unknown;
  dateRange: DateRangeQuery;
}

// Simple HKD-style currency formatter, using the currency from overview.
const formatCurrency = (value: number, currency: string) => {
  if (Number.isNaN(value) || value === null || value === undefined) {
    return "-";
  }

  try {
    return new Intl.NumberFormat("en-HK", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    // Fallback if currency code is not valid for some reason
    return `HK$${value.toLocaleString("en-HK", {
      maximumFractionDigits: 0,
    })}`;
  }
};

export const CustomerOverviewCards: React.FC<Props> = ({
  overview,
  isLoading,
  error,
  dateRange,
}) => {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, idx) => (
          <div
            key={idx}
            className="rounded-lg border bg-card p-4 animate-pulse"
          >
            <div className="h-3 w-20 rounded bg-muted" />
            <div className="mt-3 h-5 w-24 rounded bg-muted" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border bg-destructive/5 p-4 text-sm text-destructive">
        Failed to load customer overview.
      </div>
    );
  }

  if (!overview) {
    return null;
  }

  const {
    currency,
    total_invoiced,
    total_paid,
    total_outstanding,
    gmv_last_12_months,
    order_count_last_12_months,
    avg_order_value_last_12_months,
  } = overview;

  const rangeLabel =
    dateRange?.date_from && dateRange?.date_to
      ? `${dateRange.date_from} ~ ${dateRange.date_to}`
      : "Selected period";

  return (
    <TooltipProvider>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">

        {/* Total Invoiced */}
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center text-xs text-muted-foreground mb-1">
            Total Invoiced
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="ml-1 h-3 w-3 cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-[220px]">
                The total amount invoiced to this customer within the selected period.
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="text-xl font-semibold">
            {formatCurrency(total_invoiced, currency)}
          </div>
        </div>

        {/* Total Paid */}
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center text-xs text-muted-foreground mb-1">
            Total Paid
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="ml-1 h-3 w-3 cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-[220px]">
                The amount that has already been paid by the customer.
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="text-xl font-semibold">
            {formatCurrency(total_paid, currency)}
          </div>
        </div>

        {/* Total Outstanding */}
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center text-xs text-muted-foreground mb-1">
            Total Outstanding
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="ml-1 h-3 w-3 cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-[220px]">
                The remaining amount not yet paid (accounts receivable balance).
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="text-xl font-semibold text-amber-600">
            {formatCurrency(total_outstanding, currency)}
          </div>
        </div>

        {/* GMV */}
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center text-xs text-muted-foreground mb-1">
            GMV ({rangeLabel})
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="ml-1 h-3 w-3 cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-[220px]">
                Total order value created during the selected period. Based on sales orders, not invoices.
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="text-xl font-semibold">
            {formatCurrency(gmv_last_12_months, currency)}
          </div>
        </div>

        {/* Orders */}
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center text-xs text-muted-foreground mb-1">
            Orders ({rangeLabel})
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="ml-1 h-3 w-3 cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-[220px]">
                The number of sales orders placed by this customer during the selected period.
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="text-xl font-semibold">
            {order_count_last_12_months?.toLocaleString("en-HK") ?? "-"}
          </div>
        </div>

        {/* Avg Order Value */}
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center text-xs text-muted-foreground mb-1">
            Avg Order Value ({rangeLabel})
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="ml-1 h-3 w-3 cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-[220px]">
                The average value per order (GMV divided by the number of orders).
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="text-xl font-semibold">
            {formatCurrency(avg_order_value_last_12_months, currency)}
          </div>
        </div>

      </div>
    </TooltipProvider>
  );
};

export default CustomerOverviewCards;
